import { useCallback, useMemo, useRef, useState } from 'react';
import { getLocalTrialOffer } from '../config/trialPaymentOffers';
import { getPaywallContext } from '../utils/paywallContext';

export function readTrialCampaign(search) {
  const values = new URLSearchParams(search).getAll('utm_campaign');
  return values.length === 1 && values[0] && values[0].length <= 500 ? values[0] : null;
}

export function useTrialPaymentOffer(base, { enabled = true,
  pathname = window.location.pathname, search = window.location.search } = {}) {
  const eligible = enabled && ['trial-usd-v1', 'trial-eur-v1'].includes(base.offerId);
  const campaign = readTrialCampaign(search);
  const context = useMemo(() => ({ base, eligible, pathname, search, campaign,
    key: JSON.stringify([base.offerId, base.amount, base.currency, eligible, pathname, search]) }),
  [base, eligible, pathname, search, campaign]);
  const currentContext = useRef(context.key);
  currentContext.current = context.key;
  const lock = useRef(false);
  const [frozen, setFrozen] = useState(null);
  const active = frozen || context;
  const refreshRequired = useRef(false);
  const [priceChanged, setPriceChanged] = useState(false);
  // Select locally before the first render; the backend validates at /payment.
  // Frozen context keeps the selected terms stable throughout the attempt.
  const offer = useMemo(() => active.eligible
    ? { ...getLocalTrialOffer(active.base, getPaywallContext(active.pathname).paywall,
      active.campaign), trialCampaign: active.campaign } : null,
  [active.eligible, active.base, active.pathname, active.campaign]);
  const begin = useCallback((owner) => {
    if (currentContext.current !== active.key || refreshRequired.current || lock.current || window.location.pathname !== active.pathname || window.location.search !== active.search) return false;
    lock.current = { owner }; setFrozen(active); return true;
  }, [active]);
  const finish = useCallback((owner) => {
    if (lock.current && lock.current.owner !== owner) return;
    lock.current = false; setFrozen(null);
  }, []);
  const refresh = useCallback(() => {
    // An unexpected server offer change must not silently retry a stale price.
    refreshRequired.current = true;
    lock.current = false; setFrozen(null); setPriceChanged(true);
  }, []);
  const symbol = offer?.currency === 'eur' ? '€' : '$';
  return { offer, ready: !priceChanged, busy: !!frozen, begin, finish, refresh,
    reloadRequired: priceChanged,
    error: priceChanged ? 'The price has changed. Reload this page before trying again.' : '',
    pricing: offer?.paymentMode === 'split_trial_v1'
      ? { ...active.base, offerId: offer.offerId, amount: 50, purchaseValue: 0.5,
        totalAmount: 100, trialDisplay: `${symbol}1`, trialValueText: `${symbol}1` }
      : active.base };
}
